import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import configService from '../services/config';
import {
  CircularProgress,
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useMediaQuery,
  useTheme,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TimerIcon from '@mui/icons-material/Timer';
import VisibilityIcon from '@mui/icons-material/Visibility';

//  Time Format
const fmt = (iso) => !iso ? '—' : new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

//  Date Format
const fmtDate = (iso) => !iso ? '—' : new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

export default function Attendance() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { company_id, userId } = useSelector(state => state.globalVariables);

  const [attendanceList, setAttendanceList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // State for Eye (View) Modal
  const [openViewModal, setOpenViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);

  const fetchAttendance = async () => {
    try {
      const res = await configService.getAttendanceByDateRange({ company_id, employee_id: userId });
      if (res?.status || res?.data?.status) {
        const rawData = res.data?.data || res.data || [];
        setAttendanceList(rawData);
      }
    } catch (error) {
      console.error("Fetch Attendance Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAttendance(); }, [userId]);

  const handleOpenView = (item) => {
    setViewRecord(item);
    setOpenViewModal(true);
  };

  const handleCloseView = () => {
    setOpenViewModal(false);
    setViewRecord(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#6c5ce7' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, color: '#1e293b', minHeight: '100vh', bgcolor: '#f8fafc' }}>

      {/* ── Summary Cards ── */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#ffffff', borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(22, 163, 74, 0.1)', color: '#16a34a', display: 'flex' }}>
              <CalendarMonthIcon />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#16a34a' }}>{attendanceList.length}</Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>Total Days</Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#ffffff', borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(79, 142, 247, 0.1)', color: '#4f8ef7', display: 'flex' }}>
              <TimerIcon />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#4f8ef7' }}>
                {attendanceList[0]?.duration || '0 hr'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>Last Duration</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Main Section ── */}
      <Paper sx={{ p: { xs: 2, md: 3 }, bgcolor: '#ffffff', borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Attendance Records</Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>{attendanceList.length} Records found</Typography>
          </Box>

          <ButtonGroup variant="outlined" sx={{ bgcolor: '#ffffff', borderRadius: 2 }}>
            {['all', 'Present'].map((f) => (
              <Button
                key={f}
                onClick={() => setFilter(f)}
                sx={{
                  bgcolor: filter === f ? '#6c5ce7' : 'transparent',
                  color: filter === f ? '#fff' : '#475569',
                  borderColor: '#cbd5e1',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { 
                    bgcolor: filter === f ? '#5b4bc4' : '#f1f5f9',
                    borderColor: '#cbd5e1'
                  }
                }}
              >
                {f}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        {attendanceList.length === 0 ? (
          <Typography align="center" sx={{ py: 10, color: '#94a3b8' }}>No records found</Typography>
        ) : (
          isMobile ? (
            /* ── Mobile View ── */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {attendanceList.map((item, idx) => (
                <Card key={idx} sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', color: '#1e293b', boxShadow: 'none', borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: '#475569', fontWeight: 600 }}>{fmtDate(item.date)}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label="Present" size="small" sx={{ bgcolor: 'rgba(22, 163, 74, 0.1)', color: '#16a34a', fontWeight: 600 }} />
                        <IconButton size="small" onClick={() => handleOpenView(item)} sx={{ color: '#6c5ce7', bgcolor: 'rgba(108, 92, 231, 0.08)', '&:hover': { bgcolor: 'rgba(108, 92, 231, 0.15)' } }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" display="block" color="#94a3b8" sx={{ fontWeight: 500 }}>IN / OUT</Typography>
                        <Typography variant="body2" sx={{ color: '#334155' }}>{fmt(item.punch_in)} - {fmt(item.punch_out)}</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" display="block" color="#94a3b8" sx={{ fontWeight: 500 }}>Duration</Typography>
                        <Typography variant="body2" sx={{ color: '#4f8ef7', fontWeight: 600 }}>{item.duration || '—'}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            /* ── Desktop View ── */
            <TableContainer>
              <Table sx={{ '& .MuiTableCell-root': { color: '#334155', borderColor: '#e2e8f0' } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ color: '#475569', fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Punch In</TableCell>
                    <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Punch Out</TableCell>
                    <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="center" sx={{ color: '#475569', fontWeight: 600 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceList.map((item, idx) => (
                    <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' }, transition: 'background-color 0.2s' }}>
                      <TableCell sx={{ color: '#94a3b8' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>{fmtDate(item.date)}</TableCell>
                      <TableCell>{fmt(item.punch_in)}</TableCell>
                      <TableCell>{fmt(item.punch_out)}</TableCell>
                      <TableCell sx={{ color: '#4f8ef7', fontWeight: 500 }}>{item.duration}</TableCell>
                      <TableCell>
                        <Chip label="Present" sx={{ bgcolor: 'rgba(22, 163, 74, 0.1)', color: '#16a34a', fontWeight: 600, borderRadius: 1.5 }} />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenView(item)}
                          sx={{ color: '#6c5ce7', bgcolor: 'rgba(108, 92, 231, 0.08)', '&:hover': { bgcolor: 'rgba(108, 92, 231, 0.15)' } }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </Paper>

      {/* ── VIEW MODAL (WEEKLY DETAILS) ── */}
      <Dialog 
        open={openViewModal} 
        onClose={handleCloseView} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#ffffff',
            border: '1px solid #e2e8f0',
            color: '#1e293b',
            borderRadius: 4,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#1e293b' }}>
            Weekly Attendance Summary
          </Typography>
          
          <TableContainer component={Paper} sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { color: '#334155', borderColor: '#e2e8f0', py: 1.5 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc', '& th': { color: '#dc2626', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' } }}>
                  <TableCell>WEEK</TableCell>
                  <TableCell>PUNCH IN</TableCell>
                  <TableCell>PUNCH OUT</TableCell>
                  <TableCell>DURATION</TableCell>
                  <TableCell>WORK HOUR</TableCell>
                  <TableCell>SHORT HOUR</TableCell>
                  <TableCell>EXTRA HOUR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { week: 'sunday', punchIn: 'weekly off', punchOut: 'weekly off', duration: '0 HOURS', workHour: '0 HOURS', shortHour: '0 HOURS', extraHour: '0 HOURS' },
                  { week: 'monday', punchIn: '9:00 AM', punchOut: '2:00 PM', duration: '5 HOURS', workHour: '8 HOURS', shortHour: '3 HOURS', extraHour: '0 HOURS' },
                  { week: 'tuesday', punchIn: '9:00 AM', punchOut: '2:00:00 PM', duration: '5 HOURS', workHour: '8 HOURS', shortHour: '3 HOURS', extraHour: '0 HOURS' },
                  { week: 'wednesday', punchIn: '9:00 AM', punchOut: '2:00:00 PM', duration: '5 HOURS', workHour: '8 HOURS', shortHour: '3 HOURS', extraHour: '0 HOURS' },
                  { week: 'thursday', punchIn: '9:00 AM', punchOut: '2:00:00 PM', duration: '5 HOURS', workHour: '8 HOURS', shortHour: '3 HOURS', extraHour: '0 HOURS' },
                  { week: 'friday', punchIn: '9:00 AM', punchOut: '2:00:00 PM', duration: '5 HOURS', workHour: '8 HOURS', shortHour: '3 HOURS', extraHour: '0 HOURS' },
                  { week: 'saturday', punchIn: '9:00 AM', punchOut: '2:00:00 PM', duration: '5 HOURS', workHour: '8 HOURS', shortHour: '3 HOURS', extraHour: '0 HOURS' },
                ].map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f8fafc' }, transition: 'background-color 0.1s' }}>
                    <TableCell sx={{ textTransform: 'capitalize', fontWeight: 600, color: '#1e293b' }}>{row.week}</TableCell>
                    <TableCell sx={{ color: row.punchIn === 'weekly off' ? '#94a3b8' : '#334155' }}>{row.punchIn}</TableCell>
                    <TableCell sx={{ color: row.punchOut === 'weekly off' ? '#94a3b8' : '#334155' }}>{row.punchOut}</TableCell>
                    <TableCell>{row.duration}</TableCell>
                    <TableCell>{row.workHour}</TableCell>
                    <TableCell sx={{ color: row.shortHour !== '0 HOURS' ? '#ea580c' : '#334155' }}>{row.shortHour}</TableCell>
                    <TableCell>{row.extraHour}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleCloseView} sx={{ color: '#64748b', '&:hover': { color: '#1e293b', bgcolor: '#f1f5f9' }, textTransform: 'none', fontWeight: 500 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}